const EXCLUDED_AUTOCOMPLETE = ['username', 'email', 'current-password', 'new-password', 'password'];

const EXCLUDED_FIELD_PATTERN = /slug|email|password|username|phone|mobile|url|token|api[-_]?key|apikey|secret|key[-_]?id|filename|file[-_]?name|admission[-_]?no|roll[-_]?no|isbn|otp|pin|identifier|search|query/i;

/**
 * Capitalize the first character; preserve the rest of the string.
 * @param {string} value
 * @returns {string}
 */
export function capitalizeFirstLetter(value) {
  if (value == null || value === '') return value;
  const str = String(value);
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Title-case each word in a string.
 * @param {string} value
 * @returns {string}
 */
export function capitalizeWords(value) {
  if (!value) return value;
  return String(value).replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/**
 * Format registration text fields by mode.
 * @param {string} value
 * @param {'words'|'first'} [mode='words']
 * @returns {string}
 */
export function formatRegistrationText(value, mode = 'words') {
  if (mode === 'words') return capitalizeWords(value);
  return capitalizeFirstLetter(value);
}

/**
 * Whether a text input/textarea should auto-capitalize the first letter.
 * @param {object} props - Input or textarea props
 * @returns {boolean}
 */
export function shouldAutoCapitalize(props = {}) {
  if (props.capitalize === false || props['data-no-capitalize'] != null) {
    return false;
  }
  if (props.capitalize === true) {
    return true;
  }

  const type = props.type || 'text';
  if (type !== 'text') {
    return false;
  }

  const inputMode = props.inputMode || props.inputmode;
  if (inputMode === 'numeric' || inputMode === 'decimal') {
    return false;
  }

  const autoComplete = String(props.autoComplete || props.autocomplete || '').toLowerCase();
  if (EXCLUDED_AUTOCOMPLETE.some((token) => autoComplete.includes(token))) {
    return false;
  }

  const fieldKey = `${props.name || ''} ${props.id || ''}`;
  if (EXCLUDED_FIELD_PATTERN.test(fieldKey)) {
    return false;
  }

  return true;
}

/**
 * Apply first-letter capitalization when enabled for the field.
 * @param {string} value
 * @param {object} props
 * @returns {string}
 */
export function formatTextInputValue(value, props = {}) {
  if (!shouldAutoCapitalize(props)) {
    return value;
  }
  return capitalizeFirstLetter(value);
}

/**
 * Build a change event with an optionally capitalized value.
 * @param {Event} event
 * @param {object} props
 * @returns {Event}
 */
export function withCapitalizedValue(event, props = {}) {
  const nextValue = formatTextInputValue(event.target.value, props);
  if (nextValue !== event.target.value) {
    event.target.value = nextValue;
  }
  return event;
}
